/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_PRIMARY,
  Button,
  DXP_DATA_ID_FORM_ERROR,
  DialogContentProps,
  ErrorMessage,
} from '@squiz/sds';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DirectoryViewer } from '../../../../../../../../Components/DirectoryViewer/DirectoryViewer';
import {
  AccessControlListValues,
  EntityFileExplorerItem,
  useFileStore,
} from '../../../../../../../../Services/file-store';
import { FILE_EXPLORER_STRINGS } from '../../../../FileExplorer.constants';
import { useFileExplorerStore } from '../../../../FileExplorer.state';
import { ExtractArchiveDialogContentProps } from './ExtractArchiveDialogContent.props';

export const ExtractArchiveDialogContent = (props: DialogContentProps): JSX.Element => {
  const { t } = useTranslation();

  const { close, setButtonKeys, setButtons } = props;

  const itemProps = props.customProps as ExtractArchiveDialogContentProps;
  const { fileId } = itemProps;

  const labels = useMemo(() => {
    return {
      cancel: t(FILE_EXPLORER_STRINGS.cancel),
      extract: t(FILE_EXPLORER_STRINGS.extract),
      extractArchive: t(FILE_EXPLORER_STRINGS.extractArchive),
      extractErrorNotEmpty: t(FILE_EXPLORER_STRINGS.extractErrorNotEmpty),
      selectDirectoryToUnarchive: t(FILE_EXPLORER_STRINGS.selectDirectoryToUnarchive),
    };
  }, [t]);

  const { extractArchive, getDirectoryContents, listDirectories, loadingState } = useFileStore();
  const { directoryViewerSelection, fileExplorerPosition, setDirectoryContents, setDirectoryViewerSelection } =
    useFileExplorerStore();

  const [directoryList, setDirectoryList] = useState<Array<EntityFileExplorerItem>>([]);
  const [formError, setFormError] = useState<string>(``);

  useEffect(() => {
    const fetchDirectories = async (): Promise<void> => {
      const response = await listDirectories({ source: '/' });
      setDirectoryList(response.data);
    };
    fetchDirectories();
  }, [listDirectories]);

  const validateTargetDirectoryIsEmpty = useCallback(async (): Promise<boolean> => {
    const targetContents = await getDirectoryContents({ source: directoryViewerSelection as string });

    if (targetContents.data.length) {
      return false;
    }

    return true;
  }, [directoryViewerSelection, getDirectoryContents]);

  const handleExtract = useCallback(async () => {
    setFormError('');
    const targetIsEmpty = await validateTargetDirectoryIsEmpty();

    if (!targetIsEmpty) {
      setFormError(labels.extractErrorNotEmpty);
      return;
    }

    await extractArchive({
      access: AccessControlListValues.PRIVATE,
      fileId,
      target: directoryViewerSelection as string,
    });
    setDirectoryViewerSelection(null);
    const refreshList = await getDirectoryContents({ source: fileExplorerPosition });
    setDirectoryContents(refreshList.data);

    close();
  }, [
    close,
    directoryViewerSelection,
    extractArchive,
    fileExplorerPosition,
    fileId,
    getDirectoryContents,
    labels,
    setDirectoryContents,
    setDirectoryViewerSelection,
    setFormError,
    validateTargetDirectoryIsEmpty,
  ]);

  useEffect(() => {
    const cancelButton = (
      <Button
        classes="file-explorer__extract-dialog-cancel-button"
        format={BUTTON_FORMAT_SOFT}
        isDisabled={loadingState.directoryContentsLoading || loadingState.extractArchiveLoading}
        onClick={(): void => {
          setDirectoryViewerSelection(null);
          close();
        }}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    const confirmButton = (
      <Button
        classes="file-explorer__extract-dialog-confirm-button"
        format={BUTTON_FORMAT_STRONG}
        isDisabled={!directoryViewerSelection}
        isLoading={loadingState.directoryContentsLoading || loadingState.extractArchiveLoading}
        onClick={async (): Promise<void> => handleExtract()}
        text={labels.extract}
        theme={BUTTON_THEME_PRIMARY}
      />
    );

    setButtonKeys([`cancel`, `confirm`]);
    setButtons([cancelButton, confirmButton]);
  }, [
    close,
    directoryViewerSelection,
    handleExtract,
    labels,
    loadingState,
    setButtonKeys,
    setButtons,
    setDirectoryViewerSelection,
  ]);

  return (
    <div className="file-explorer__dialog-content extract">
      <div className="sds-dialog__heading custom">
        <span>{labels.extractArchive}</span>
      </div>
      <p className="file-explorer__dialog-text">{labels.selectDirectoryToUnarchive}</p>
      <DirectoryViewer
        initialData={directoryList}
        setValue={setDirectoryViewerSelection}
        value={directoryViewerSelection}
      />
      {formError && (
        <div className="sds-form-error" data-id={DXP_DATA_ID_FORM_ERROR}>
          <ErrorMessage message={formError} />
        </div>
      )}
    </div>
  );
};
